import { CODIFF_BASE_URL, GITLAB_BASE_URL } from "./constants";

export const formatDate = (dateString) => {
	const date = new Date(dateString);
	const now = new Date();
	const diffMs = now - date;
	const diffHours = diffMs / (1000 * 60 * 60);

	if (diffHours < 24) {
		if (diffHours < 1) {
			const mins = Math.round(diffMs / (1000 * 60));
			return `${mins} minutes ago`;
		}
		return `${Math.round(diffHours)}h ago`;
	}
	return date.toLocaleDateString();
};

// Returns the URL only if it uses a safe http(s) scheme, otherwise undefined.
// Guards against javascript:/data: URLs being used as bare hrefs.
export const safeHttpUrl = (url) => {
	if (!url || typeof url !== "string") return undefined;
	return /^https?:\/\//i.test(url.trim()) ? url : undefined;
};

// Rewrite a GitLab MR URL to its Codiff equivalent, e.g.
// https://gitlab.cfdata.org/g/p/-/merge_requests/1
//   -> https://codiff.cloudflare.dev/g/p/-/merge_requests/1
// Anything that isn't a GitLab MR URL is returned untouched, so callers can
// apply this unconditionally.
export const toCodiffUrl = (url) => {
	if (!url || typeof url !== "string") return url;
	if (!url.startsWith(`${GITLAB_BASE_URL}/`)) return url;

	const path = url.slice(GITLAB_BASE_URL.length);
	if (!/\/-\/merge_requests\/\d+$/.test(path)) return url;

	return `${CODIFF_BASE_URL}${path}`;
};

// Resolve the URL an MR link should point at, honouring the Codiff setting.
export const mrLinkUrl = (url, useCodiffLinks) =>
	useCodiffLinks ? toCodiffUrl(url) : url;

export const formatProjectName = (reference) => {
	const parts = reference.split("/");
	if (parts.length <= 1) return reference;

	const projectPart = parts.join("/").split("!")[0];
	return projectPart;
};

export const isStale = (mr) => {
	if (!mr || mr.state !== "opened") return false;

	const now = new Date();
	const updatedAt = new Date(mr.updatedAt);
	const daysSinceUpdate = (now - updatedAt) / (1000 * 60 * 60 * 24);

	return daysSinceUpdate > 14;
};

// Escape a string for safe inclusion in HTML.
const escapeHtml = (str) =>
	String(str)
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");

// Build the visible label for a copied MR link, e.g. "group/project #1234: Title".
export const formatMrLinkLabel = (mr) => {
	const project = formatProjectName(mr.projectPath || "");
	const prefix = project ? `${project} ` : "";
	return `${prefix}#${mr.iid}: ${mr.title}`;
};

// Copy an MR as a rich-text hyperlink so it pastes as a clickable link
// (with custom display text) into editors like Google Docs.
// Falls back to copying plain text where the rich clipboard API is unavailable.
export const copyMrLink = async (mr, useCodiffLinks = false) => {
	const label = formatMrLinkLabel(mr);
	const url = mrLinkUrl(mr.webUrl, useCodiffLinks);
	const html = `<a href="${escapeHtml(url)}">${escapeHtml(label)}</a>`;
	const text = `${label} (${url})`;

	try {
		if (
			typeof ClipboardItem !== "undefined" &&
			navigator.clipboard?.write
		) {
			await navigator.clipboard.write([
				new ClipboardItem({
					"text/html": new Blob([html], { type: "text/html" }),
					"text/plain": new Blob([text], { type: "text/plain" }),
				}),
			]);
			return true;
		}

		if (navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(text);
			return true;
		}
	} catch (err) {
		console.error(
			`Failed to copy MR link (iid=${mr.iid}, project=${mr.projectPath}, url=${url}):`,
			err,
		);
	}

	return false;
};

export const getCIStatusInfo = (pipeline) => {
	if (!pipeline) return null;

	switch (pipeline.status) {
		case "success":
			return {
				icon: "CheckCircle2",
				color: "text-green-500 dark:text-green-400",
			};
		case "failed":
			return { icon: "XCircle", color: "text-red-500 dark:text-red-400" };
		case "pending":
		case "running":
			return { icon: "CircleDot", color: "text-blue-500 dark:text-blue-400" };
		default:
			return null;
	}
};
